import React from "react";
import { ALL_STATUSES, STATUS_LABEL_MAP } from "@/modules/snapshots/snapshotEvents.consts";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { FiltersState } from "@/modules/snapshots/snapshotEvents/snapshotEventsAnalytics";

interface FilterBarProps {
  filters: FiltersState;
  setFilters: (filters: FiltersState) => void;
  actionTakers: Array<string>;
  creators: Array<{ id: string | null; name: string }>;
  collections: Array<{ id: string | null; name: string }>;
  eventTypes: Array<{ id: string | null; name: string }>;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  setFilters,
  actionTakers,
  creators,
  collections,
  eventTypes,
}) => (
  <div className="mb-4 rounded-lg border border-border bg-white p-4 shadow-sm">
    <div className="flex flex-wrap items-center gap-4">
      {/* Status Legend Card */}
      <div className="flex items-center gap-3 rounded-md border border-border bg-muted px-3 py-2 text-xs">
        <div className="flex items-center gap-1">
          <div className="h-2.5 w-2.5 rounded-full border border-white bg-success shadow-sm" />
          <span className="text-xs font-normal text-secondary-foreground">Done</span>
        </div>

        <div className="flex items-center gap-1">
          <div className="h-2.5 w-2.5 rounded-full border border-white bg-info shadow-sm" />
          <span className="text-xs font-normal text-secondary-foreground">In progress</span>
        </div>

        <div className="flex items-center gap-1">
          <div className="h-2.5 w-2.5 rounded-full border border-white bg-danger shadow-sm" />
          <span className="text-xs font-normal text-secondary-foreground">Overdue</span>
        </div>
      </div>

      {/* Document Name Filter */}
      <input
        type="text"
        value={filters.documentName ?? undefined}
        onChange={e => setFilters({ ...filters, documentName: e.target.value })}
        placeholder="Search documents..."
        className="min-w-[400px] rounded-md border border-border bg-white px-3 py-1.5 text-sm"
      />

      {/* Action Taker Filter */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="flex items-center">
            {filters.actionTaker || "All action takers"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-48">
          <DropdownMenuItem onClick={() => setFilters({ ...filters, actionTaker: null })}>
            All action takers
          </DropdownMenuItem>
          {actionTakers.map(actionTaker => (
            <DropdownMenuItem key={actionTaker} onClick={() => setFilters({ ...filters, actionTaker })}>
              {actionTaker}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Status Filter */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="flex items-center">
            {filters.status && filters.status in STATUS_LABEL_MAP
              ? STATUS_LABEL_MAP[filters.status as (typeof ALL_STATUSES)[number]]
              : "All statuses"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-48">
          <DropdownMenuItem onClick={() => setFilters({ ...filters, status: null })}>All statuses</DropdownMenuItem>
          {ALL_STATUSES.map(status => (
            <DropdownMenuItem key={status} onClick={() => setFilters({ ...filters, status })}>
              {STATUS_LABEL_MAP[status]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Creator Filter */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="flex items-center">
            {creators.find(({ id }) => id === filters.creator)?.name || "All creators"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-48">
          <DropdownMenuItem onClick={() => setFilters({ ...filters, creator: null })}>All creators</DropdownMenuItem>
          {creators.map(creator => (
            <DropdownMenuItem key={creator.id} onClick={() => setFilters({ ...filters, creator: creator.id })}>
              {creator.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Collection Filter */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="flex items-center">
            {collections.find(({ id }) => id === filters.collection)?.name || "All collections"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-48">
          {collections.map(collection => (
            <DropdownMenuItem key={collection.id} onClick={() => setFilters({ ...filters, collection: collection.id })}>
              {collection.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Event Type Filter */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="flex items-center">
            {eventTypes.find(({ id }) => id === filters.event_type)?.name || "All event types"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {eventTypes.map(event_type => (
            <DropdownMenuItem key={event_type.id} onClick={() => setFilters({ ...filters, event_type: event_type.id })}>
              {event_type.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Start Date Filter */}
      <input
        type={filters.startDate ? "date" : "text"}
        value={filters.startDate || "Start Date"}
        onChange={e => setFilters({ ...filters, startDate: e.target.value })}
        placeholder="Start Date"
        className={`min-w-[140px] rounded-lg border border-border bg-white px-3 py-1.5 text-sm ${
          filters.startDate ? "text-black" : "text-gray-400"
        }`}
        onFocus={e => {
          if (!filters.startDate) {
            e.target.type = "date";
            e.target.showPicker();
          }
        }}
        onBlur={e => {
          if (!e.target.value) {
            e.target.type = "text";
          }
        }}
      />

      {/* End Date Filter */}
      <input
        type={filters.endDate ? "date" : "text"}
        value={filters.endDate || "End Date"}
        onChange={e => setFilters({ ...filters, endDate: e.target.value })}
        placeholder="End Date"
        className={`min-w-[140px] rounded-lg border border-border bg-white px-3 py-1.5 text-sm ${
          filters.startDate ? "text-black" : "text-gray-400"
        }`}
        onFocus={e => {
          if (!filters.endDate) {
            e.target.type = "date";
            e.target.showPicker();
          }
        }}
        onBlur={e => {
          if (!e.target.value) {
            e.target.type = "text";
          }
        }}
      />

      {/* Clear Filters Button */}
      <button
        onClick={() =>
          setFilters({
            actionTaker: "",
            status: "",
            creator: "",
            collection: "",
            documentName: "",
            startDate: "",
            endDate: "",
            event_type: "",
            sort_field: "name",
            sort_order: "asc",
          })
        }
        className="ml-auto cursor-pointer rounded-lg border-none bg-primary px-4 py-2 text-[13px] font-medium text-white"
      >
        Clear All
      </button>
    </div>
  </div>
);
